import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { questions } from '../data/questions'
import { createPracticeQuestionBank } from '../data/practiceQuestionBank'
import { examBlueprints } from '../data/examBlueprints'
import { KNOWLEDGE, type AppUser, type Attempt, type AttemptUpdate, type Exam, type KnowledgeCode, type Question } from '../types'
import { QuestionCard } from '../components/QuestionCard'
import { pickAdaptive } from '../lib/assessment'
import { getMirrorSprint, PENDING_MIRROR_TARGET_KEY, type LearningTarget, type MirrorSprint } from '../lib/learning'
import { clearPracticeSession, loadPracticeSession, savePracticeSession } from '../lib/practiceSessions'
import { gradeHostedPracticeAnswer, isCloudflarePracticeConfigured, saveHostedReflection, startHostedPracticeSession } from '../lib/generatorApi'

type Props = {
  attempts: Attempt[]
  addAttempt: (attempt: Attempt) => void
  updateAttempt: (id: string, update: AttemptUpdate) => void
  go?: (x: string) => void
  user?: AppUser | null
}

export function Practice({ attempts, addAttempt, updateAttempt, go, user }: Props) {
  const restoredSession = useMemo(() => loadPracticeSession(), [])
  const [exam, setExam] = useState<Exam | 'ALL'>(restoredSession?.exam ?? 'ALL')
  const [topic, setTopic] = useState(restoredSession?.topic ?? 'ALL')
  const [topicGroup, setTopicGroup] = useState<KnowledgeCode | 'ALL'>(() => {
    const restoredTopic = restoredSession?.topic
    return restoredTopic && restoredTopic !== 'ALL'
      ? questions.find(question => question.topic === restoredTopic)?.knowledge[0] ?? 'logic'
      : 'logic'
  })
  const [session, setSession] = useState<Question[]>(() => restoredSession?.questions ?? pickAdaptive(createPracticeQuestionBank(questions), attempts, 10))
  const [sessionType, setSessionType] = useState<'adaptive' | 'mirror'>('adaptive')
  const [sprint, setSprint] = useState<MirrorSprint | null>(null)
  const [idx, setIdx] = useState(restoredSession?.index ?? 0)
  const [isComplete, setIsComplete] = useState(false)
  const [settingsCollapsed, setSettingsCollapsed] = useState(true)
  const restoredSessionPending = useRef(Boolean(restoredSession))
  const [hostedSessionId, setHostedSessionId] = useState<string | null>(null)
  const hostedRequestRef = useRef<string | null>(null)
  // The Worker currently contains reviewed HSA/V-ACT templates only. TSA uses
  // the seed-locked local bank so it never silently receives another exam's items.
  const useHostedGenerator = exam !== 'TSA' && topicGroup === 'ALL' && user?.provider === 'firebase' && isCloudflarePracticeConfigured()

  const topicGroups = useMemo(() => {
    const filteredByExam = questions.filter(q => exam === 'ALL' || q.exam === exam)
    return (Object.entries(KNOWLEDGE) as Array<[KnowledgeCode, string]>).map(([code, label]) => {
      // A question can have secondary knowledge tags for analytics. Navigation
      // stays tidy by filing it under its first (curricular) knowledge area.
      const groupQuestions = filteredByExam.filter(question => question.knowledge[0] === code)
      return {
        code,
        label,
        topicCount: new Set(groupQuestions.map(question => question.topic)).size,
        questionCount: groupQuestions.length,
      }
    }).filter(group => group.topicCount > 0)
  }, [exam])

  const topicRows = useMemo(() => {
    const filtered = questions.filter(question =>
      (exam === 'ALL' || question.exam === exam)
      && (topicGroup === 'ALL' || question.knowledge[0] === topicGroup),
    )
    return Array.from(filtered.reduce((rows, question) => {
      rows.set(question.topic, (rows.get(question.topic) ?? 0) + 1)
      return rows
    }, new Map<string, number>())).map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name, 'vi'))
  }, [exam, topicGroup])

  const startAdaptive = useCallback(async () => {
    if (useHostedGenerator) {
      try {
        const hosted = await startHostedPracticeSession({ exam, topic })
        setSession(hosted.questions)
        setHostedSessionId(hosted.session.id)
        setSessionType('adaptive')
        setSprint(null)
        setIdx(hosted.session.currentIndex)
        setIsComplete(false)
        clearPracticeSession()
        return
      } catch (error) {
        console.warn('Không thể tạo lượt luyện cloud; dùng kho local-demo:', error)
      }
    }
    const practiceBank = createPracticeQuestionBank(questions)
    const pool = pickAdaptive(
      practiceBank.filter(q => (exam === 'ALL' || q.exam === exam) && (topicGroup === 'ALL' || q.knowledge[0] === topicGroup) && (topic === 'ALL' || q.topic === topic) && (exam !== 'TSA' || q.difficulty >= 4 || q.cognitiveLevel === 'M3')),
      attempts,
      10,
    )
    setSession(pool)
    setSessionType('adaptive')
    setSprint(null)
    setHostedSessionId(null)
    setIdx(0)
    setIsComplete(false)
  }, [attempts, exam, topic, topicGroup, useHostedGenerator])

  const startMirror = useCallback((target?: LearningTarget) => {
    const nextSprint = getMirrorSprint(questions, attempts, target)
    if (!nextSprint) return false
    setSession(nextSprint.questions)
    setSessionType('mirror')
    setHostedSessionId(null)
    setSprint(nextSprint)
    setIdx(0)
    setIsComplete(false)
    clearPracticeSession()
    return true
  }, [attempts])

  // Filter changes deliberately create a new session. Recording an answer does not,
  // so reflection is never interrupted by the adaptive selector.
  useEffect(() => {
    if (useHostedGenerator) return
    if (restoredSessionPending.current) {
      restoredSessionPending.current = false
      return
    }
    void startAdaptive()
  }, [exam, topic, topicGroup, useHostedGenerator]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!useHostedGenerator || !user?.uid) return
    const requestKey = `${user.uid}:${exam}:${topic}:${topicGroup}`
    if (hostedRequestRef.current === requestKey) return
    hostedRequestRef.current = requestKey
    void startAdaptive()
  }, [exam, topic, topicGroup, useHostedGenerator, user?.uid]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sessionType !== 'adaptive' || hostedSessionId) return
    if (isComplete || !session.length) {
      clearPracticeSession()
      return
    }
    savePracticeSession({ exam, topic, questions: session, index: idx, createdAt: new Date().toISOString() })
  }, [exam, hostedSessionId, idx, isComplete, session, sessionType, topic])

  useEffect(() => {
    try {
      const serialized = sessionStorage.getItem(PENDING_MIRROR_TARGET_KEY)
      if (!serialized) return
      sessionStorage.removeItem(PENDING_MIRROR_TARGET_KEY)
      startMirror(JSON.parse(serialized) as LearningTarget)
    } catch {
      sessionStorage.removeItem(PENDING_MIRROR_TARGET_KEY)
    }
  }, []) // A dashboard action is consumed once when this route mounts.

  const q = session[idx]
  const title = sessionType === 'mirror' ? sprint?.title ?? 'Mirror Sprint' : 'Luyện thích nghi'
  const description = sessionType === 'mirror'
    ? sprint?.rationale
    : 'Mỗi câu được chọn theo năng lực cần đo. Sau câu sai, hãy khoanh nguyên nhân để hệ thống chọn cách ôn phù hợp.'

  const moveNext = () => {
    if (idx + 1 >= session.length) setIsComplete(true)
    else setIdx(current => current + 1)
  }

  return (
    <div className={['exam-layout', 'practice-page', settingsCollapsed ? 'practice-settings-collapsed' : 'practice-settings-open'].join(' ')}>
      <section className="practice-compact-header" aria-label="Thiết lập lượt luyện">
        <button
          type="button"
          className="practice-controls-toggle"
          onClick={() => setSettingsCollapsed(current => !current)}
          aria-expanded={!settingsCollapsed}
          aria-label={settingsCollapsed ? 'Mở thiết lập lượt luyện' : 'Thu gọn thiết lập lượt luyện'}
          title={settingsCollapsed ? 'Mở thiết lập' : 'Thu gọn thiết lập'}
        >
          {settingsCollapsed ? '›' : '‹'}
        </button>
        <div className="practice-title-block">
          <span className={`tag-pill ${sessionType === 'mirror' ? 'orange' : 'green'}`}>
            {sessionType === 'mirror' ? 'SỬA LỖI CÓ CHỦ ĐÍCH' : 'LUYỆN THEO NĂNG LỰC'}
          </span>
          <div>
            <h1>{title}</h1>
            <p className="text-muted">{description}</p>
          </div>
        </div>
        <div className="practice-inline-controls">
          <label>
            <span className="practice-filter-label">Kỳ thi</span>
            <select aria-label="Kỳ thi" value={exam} onChange={event => { setExam(event.target.value as Exam | 'ALL'); setTopicGroup('ALL'); setTopic('ALL') }} disabled={sessionType === 'mirror'}>
              <option value="ALL">Tất cả kỳ thi</option>
              {(Object.values(examBlueprints)).map(blueprint => <option key={blueprint.code} value={blueprint.code} disabled={!blueprint.enabled}>{blueprint.enabled ? blueprint.label : `${blueprint.label} — chờ kho nội dung có quyền sử dụng`}</option>)}
            </select>
          </label>
          <label>
            <span className="practice-filter-label">Mảng kiến thức</span>
            <select aria-label="Mảng kiến thức" value={topicGroup} onChange={event => { setTopicGroup(event.target.value as KnowledgeCode | 'ALL'); setTopic('ALL') }} disabled={sessionType === 'mirror'}>
              <option value="ALL">Tất cả mảng</option>
              {topicGroups.map(group => <option key={group.code} value={group.code}>{group.label} · {group.topicCount} chuyên đề</option>)}
            </select>
          </label>
          <label>
            <span className="practice-filter-label">Chuyên đề</span>
            <select aria-label="Chuyên đề" value={topic} onChange={event => setTopic(event.target.value)} disabled={sessionType === 'mirror' || topicGroup === 'ALL'}>
              <option value="ALL">{topicGroup === 'ALL' ? 'Chọn mảng trước để xem chi tiết' : `Tất cả chuyên đề (${topicRows.length})`}</option>
              {topicGroup !== 'ALL' && topicRows.map(item => <option key={item.name} value={item.name}>{item.name} · {item.count} câu</option>)}
            </select>
          </label>
          <button className="btn btn-secondary" onClick={() => void startAdaptive()}>Lượt mới</button>
          <button className="btn btn-primary" onClick={() => startMirror()} disabled={!attempts.length}>Mirror Sprint</button>
          <button className="btn btn-ghost practice-roadmap-btn" onClick={() => go?.('dashboard')}>Đường thoát điểm</button>
        </div>
      </section>

      {!q || isComplete ? (
        <section className="card practice-complete text-center">
          <div className="feature-icon mx-auto mb-4">{sessionType === 'mirror' ? '🧭' : '🏆'}</div>
          <h2>{sessionType === 'mirror' ? 'Bạn đã hoàn thành một vòng sửa lỗi.' : 'Hoàn thành lượt luyện.'}</h2>
          <p className="text-muted">
            {sessionType === 'mirror'
              ? 'Kết quả đã được đưa vào lịch ôn 1–3–7 ngày. Hãy quay lại dashboard để xem mốc tiếp theo.'
              : 'Hãy xem dashboard để chọn lỗ hổng có tác động lớn nhất cho lượt tiếp theo.'}
          </p>
          <div className="flex justify-center gap-3">
            <button className="btn btn-primary" onClick={() => go?.('dashboard')}>Xem đường thoát điểm</button>
            <button className="btn btn-secondary" onClick={() => void startAdaptive()}>Luyện lượt khác</button>
          </div>
        </section>
      ) : (
        <>
          <main className="question-area">
            <QuestionCard
              key={q.id}
              q={q}
              session={sessionType === 'mirror' ? 'mirror-sprint' : 'practice'}
              onDone={addAttempt}
              onReflect={(id, misconception) => {
                updateAttempt(id, { misconception })
                if (hostedSessionId) void saveHostedReflection(id, misconception).catch(error => console.warn('Không thể lưu phản tư Cloudflare:', error))
              }}
              onContinue={moveNext}
              onGrade={hostedSessionId ? input => gradeHostedPracticeAnswer({
                sessionId: hostedSessionId,
                instanceId: input.questionId,
                answer: input.answer,
                elapsedSeconds: input.elapsedSeconds,
                confidence: input.confidence,
                nextIndex: Math.min(idx + 1, session.length - 1),
              }) : undefined}
            />
          </main>
          <nav className="sticky-bottom-bar-wrapper practice-bottom-bar" aria-label="Tiến độ lượt luyện">
            <div className="sticky-bottom-bar">
              <span className="practice-progress-count">Câu {idx + 1}/{session.length}</span>
              <div className="q-grid-horizontal">
                {session.map((item, index) => (
                  <button
                    key={item.id}
                    className={`q-badge ${index === idx ? 'active' : ''} ${index < idx ? 'answered' : ''}`}
                    onClick={() => setIdx(index)}
                    aria-label={`Câu ${index + 1}`}
                    aria-current={index === idx ? 'step' : undefined}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
              {sessionType === 'mirror'
                ? <span className="text-muted text-sm">Mốc ôn {sprint?.target.reviewStage ?? 0}/3</span>
                : <span className="text-muted text-sm">Mỗi lượt tạo một bộ biến thể mới</span>}
            </div>
          </nav>
        </>
      )}
    </div>
  )
}
